import { useState, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { departmentService, employeeService } from '@/services/api'
import api from '@/services/api'
import {
  Building2, Users, GraduationCap, Compass,
  Plus, Edit, Trash2, Search, Briefcase,
  ShieldCheck, LayoutGrid, ChevronRight,
  List, Grid3X3,
} from 'lucide-react'
import DepartmentModal from '@/components/departments/DepartmentModal'
import DirectionModal from '@/components/departments/DirectionModal'
import ClassModal from '@/components/departments/ClassModal'
import PositionModal from '@/components/departments/PositionModal'
import Modal from '@/components/common/Modal'
import toast from 'react-hot-toast'
import clsx from 'clsx'

// ── Stat Card ────────────────────────────────────────────────
function SummaryStat({ label, value, colorClass, icon: Icon }) {
  const themes = {
    indigo: 'text-indigo-600 bg-indigo-50 border-indigo-100',
    emerald: 'text-emerald-600 bg-emerald-50 border-emerald-100',
    amber: 'text-amber-600 bg-amber-50 border-amber-100',
    violet: 'text-violet-600 bg-violet-50 border-violet-100',
  }
  return (
    <div className="bg-white border border-slate-200 rounded-[24px] p-6 flex items-center gap-5 shadow-sm">
      <div className={clsx('p-3.5 rounded-2xl border transition-transform duration-500 hover:scale-110', themes[colorClass])}>
        <Icon size={24} strokeWidth={2.5} />
      </div>
      <div>
        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-1">{label}</p>
        <p className="text-3xl font-black text-slate-900 leading-none tracking-tighter">{value}</p>
      </div>
    </div>
  )
}

// ── Action Button ────────────────────────────────────────────
function ActionBtn({ onClick, icon: Icon, title, color = 'slate' }) {
  const colors = {
    slate: 'text-slate-400 hover:text-slate-700 hover:bg-white hover:border-slate-200',
    indigo: 'text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 hover:border-indigo-100',
    emerald: 'text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 hover:border-emerald-100',
    rose: 'text-slate-400 hover:text-rose-600 hover:bg-rose-50 hover:border-rose-100',
    amber: 'text-slate-400 hover:text-amber-600 hover:bg-amber-50 hover:border-amber-100',
  }
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick() }}
      className={clsx('w-8 h-8 flex items-center justify-center rounded-lg transition-all border border-transparent', colors[color])}
      title={title}
    >
      <Icon size={14} />
    </button>
  )
}

// ── Filter Tabs ──────────────────────────────────────────────
function FilterTabs({ value, onChange, tabs }) {
  return (
    <div className="flex bg-slate-100 rounded-xl p-1 gap-1">
      {tabs.map(tab => (
        <button
          key={tab.value}
          onClick={() => onChange(tab.value)}
          className={clsx(
            'px-4 py-2 rounded-lg text-[11px] font-black uppercase tracking-wider transition-all',
            value === tab.value
              ? 'bg-white text-slate-900 shadow-sm'
              : 'text-slate-400 hover:text-slate-600'
          )}
        >
          {tab.label} {tab.count !== undefined && <span className="text-[9px] ml-1 opacity-60">({tab.count})</span>}
        </button>
      ))}
    </div>
  )
}

// ── View Toggle ──────────────────────────────────────────────
function ViewToggle({ view, onChange }) {
  return (
    <div className="flex bg-slate-100 rounded-xl p-1 gap-1">
      <button
        onClick={() => onChange('kanban')}
        className={clsx(
          'p-2 rounded-lg transition-all',
          view === 'kanban' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-400 hover:text-slate-600'
        )}
        title="Канбан"
      >
        <Grid3X3 size={16} />
      </button>
      <button
        onClick={() => onChange('list')}
        className={clsx(
          'p-2 rounded-lg transition-all',
          view === 'list' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-400 hover:text-slate-600'
        )}
        title="Список"
      >
        <List size={16} />
      </button>
    </div>
  )
}

// ── Kanban Card ──────────────────────────────────────────────
function DeptKanbanCard({ dept, navigate, onEdit, onDelete, onAddDirection, onAddPosition }) {
  const directionsCount = (dept.directions || []).length
  const isKafedra = (dept.department_type || 'kafedra') === 'kafedra'
  let groupsCount = 0, studentsCount = 0
  ;(dept.directions || []).forEach(dir => {
    groupsCount += (dir.groups || []).length
    ;(dir.groups || []).forEach(g => { studentsCount += g.students_count || 0 })
  })

  return (
    <div
      className="bg-white border border-slate-200 rounded-2xl shadow-sm hover:shadow-md transition-all flex flex-col cursor-pointer group"
      onClick={() => isKafedra ? navigate(`/departments/${dept.id}`) : navigate(`/analytics/employees-departments/${dept.id}`)}
    >
      <div className="p-4">
        <div className="flex items-start gap-3 mb-3">
          <div className={clsx('w-10 h-10 rounded-xl border flex items-center justify-center shrink-0',
            isKafedra ? 'bg-amber-50 text-amber-600 border-amber-100' : 'bg-blue-50 text-blue-600 border-blue-100'
          )}>
            <Building2 size={20} strokeWidth={2.2} />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-black text-slate-900 leading-tight group-hover:text-indigo-600 transition-colors line-clamp-2">{dept.name}</h4>
            {dept.short_name && (
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mt-0.5">{dept.short_name}</p>
            )}
          </div>
          <ChevronRight size={16} className="text-slate-300 group-hover:text-indigo-400 shrink-0 mt-1 transition-colors" />
        </div>

        <div className="flex gap-2 flex-wrap">
          {isKafedra ? (
            <>
              <span className="text-[9px] font-black px-2 py-1 rounded-full bg-indigo-50 text-indigo-600 border border-indigo-100">
                {directionsCount} направл.
              </span>
              <span className="text-[9px] font-black px-2 py-1 rounded-full bg-violet-50 text-violet-600 border border-violet-100">
                {groupsCount} групп
              </span>
              <span className="text-[9px] font-black px-2 py-1 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-100">
                {studentsCount} студ.
              </span>
            </>
          ) : (
            <span className="text-[9px] font-black px-2 py-1 rounded-full bg-blue-50 text-blue-600 border border-blue-100">
              Отдел
            </span>
          )}
        </div>
      </div>

      <div className="border-t border-slate-100 px-3 py-2 flex items-center justify-between">
        <span className={clsx(
          'text-[8px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full border',
          dept.is_active ? 'bg-emerald-50 text-emerald-500 border-emerald-100' : 'bg-slate-50 text-slate-400 border-slate-200'
        )}>
          {dept.is_active ? 'Актив' : 'Архив'}
        </span>
        <div className="flex gap-0.5">
          {isKafedra && <ActionBtn onClick={() => onAddDirection(dept.id)} icon={Plus} title="Добавить направление" color="emerald" />}
          {!isKafedra && <ActionBtn onClick={() => onAddPosition(dept.id)} icon={Briefcase} title="Добавить должность" color="indigo" />}
          <ActionBtn onClick={() => onEdit(dept)} icon={Edit} title="Редактировать" color="amber" />
          <ActionBtn onClick={() => onDelete(dept, 'dept')} icon={Trash2} title="Удалить" color="rose" />
        </div>
      </div>
    </div>
  )
}

// ── List Row ─────────────────────────────────────────────────
function DeptListRow({ dept, navigate, onEdit, onDelete, onAddDirection, onAddPosition }) {
  const directionsCount = (dept.directions || []).length
  const isKafedra = (dept.department_type || 'kafedra') === 'kafedra'
  let groupsCount = 0, studentsCount = 0
  ;(dept.directions || []).forEach(dir => {
    groupsCount += (dir.groups || []).length
    ;(dir.groups || []).forEach(g => { studentsCount += g.students_count || 0 })
  })

  return (
    <div
      className="bg-white border border-slate-200 rounded-2xl px-5 py-4 flex items-center gap-4 hover:shadow-md transition-all cursor-pointer group"
      onClick={() => isKafedra ? navigate(`/departments/${dept.id}`) : navigate(`/analytics/employees-departments/${dept.id}`)}
    >
      <div className={clsx('w-10 h-10 rounded-xl border flex items-center justify-center shrink-0',
        isKafedra ? 'bg-amber-50 text-amber-600 border-amber-100' : 'bg-blue-50 text-blue-600 border-blue-100'
      )}>
        <Building2 size={20} />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-bold text-slate-900 truncate group-hover:text-indigo-600 transition-colors">{dept.name}</h4>
        <p className="text-[10px] text-slate-400 font-medium">{dept.short_name || dept.head_name || 'Не указано'}</p>
      </div>
      <div className="hidden sm:flex gap-3 items-center shrink-0">
        {isKafedra ? (
          <>
            <div className="text-center px-3">
              <div className="text-sm font-black text-indigo-600">{directionsCount}</div>
              <div className="text-[8px] font-bold text-slate-400 uppercase">Направл.</div>
            </div>
            <div className="text-center px-3">
              <div className="text-sm font-black text-violet-600">{groupsCount}</div>
              <div className="text-[8px] font-bold text-slate-400 uppercase">Групп</div>
            </div>
            <div className="text-center px-3">
              <div className="text-sm font-black text-emerald-600">{studentsCount}</div>
              <div className="text-[8px] font-bold text-slate-400 uppercase">Студ.</div>
            </div>
          </>
        ) : (
          <span className="text-[10px] font-black px-3 py-1 rounded-full bg-blue-50 text-blue-600 border border-blue-100">Отдел</span>
        )}
      </div>
      <div className="flex gap-0.5 shrink-0">
        {isKafedra && <ActionBtn onClick={() => onAddDirection(dept.id)} icon={Plus} title="Добавить направление" color="emerald" />}
        {!isKafedra && <ActionBtn onClick={() => onAddPosition(dept.id)} icon={Briefcase} title="Добавить должность" color="indigo" />}
        <ActionBtn onClick={() => onEdit(dept)} icon={Edit} title="Редактировать" color="amber" />
        <ActionBtn onClick={() => onDelete(dept, 'dept')} icon={Trash2} title="Удалить" color="rose" />
      </div>
      <ChevronRight size={16} className="text-slate-300 group-hover:text-indigo-400 shrink-0 transition-colors" />
    </div>
  )
}

// ── Main Page ────────────────────────────────────────────────
export default function DepartmentsPage() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('all')
  const [view, setView] = useState('kanban')

  const [deptModal, setDeptModal] = useState({ open: false, data: null })
  const [directionModal, setDirectionModal] = useState({ open: false, data: null, deptId: null })
  const [classModal, setClassModal] = useState({ open: false, data: null, directionId: null })
  const [positionModal, setPositionModal] = useState({ open: false, data: null, deptId: null })
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null, name: '', type: '' })
  const [deleting, setDeleting] = useState(false)

  const { data: hierarchy, isLoading } = useQuery({
    queryKey: ['departments-hierarchy'],
    queryFn: () => departmentService.hierarchy().then((r) => r.data),
  })

  const { data: employeesData } = useQuery({
    queryKey: ['employees-count'],
    queryFn: () => employeeService.list({ page_size: 1, status: 'active' }).then((r) => r.data),
  })
  const employeesCount = employeesData?.count ?? 0

  const stats = useMemo(() => {
    if (!hierarchy) return { depts: 0, directions: 0, groups: 0, students: 0, kafedras: 0, otdels: 0 }
    let directions = 0, groups = 0, students = 0, kafedras = 0, otdels = 0
    hierarchy.forEach((dept) => {
      const dirs = dept.directions || []
      if ((dept.department_type || 'kafedra') === 'kafedra') { kafedras++ } else { otdels++ }
      directions += dirs.length
      dirs.forEach((dir) => {
        const grps = dir.groups || []
        groups += grps.length
        grps.forEach((g) => { students += g.students_count || 0 })
      })
    })
    return { depts: hierarchy.length, directions, groups, students, kafedras, otdels }
  }, [hierarchy])

  const filteredHierarchy = useMemo(() => {
    if (!hierarchy) return []
    let list = hierarchy
    if (filter === 'kafedra') {
      list = list.filter(d => (d.department_type || 'kafedra') === 'kafedra')
    } else if (filter === 'otdel') {
      list = list.filter(d => d.department_type === 'otdel')
    }
    if (search) {
      const q = search.toLowerCase()
      list = list.filter(d => d.name.toLowerCase().includes(q) || (d.short_name || '').toLowerCase().includes(q))
    }
    return list
  }, [hierarchy, search, filter])

  const handleDelete = async () => {
    setDeleting(true)
    try {
      if (deleteModal.type === 'dept') {
        await api.delete(`/departments/departments/${deleteModal.id}/`)
      } else if (deleteModal.type === 'direction') {
        await api.delete(`/departments/directions/${deleteModal.id}/`)
      } else {
        await api.delete(`/departments/classes/${deleteModal.id}/`)
      }
      qc.invalidateQueries(['departments-hierarchy'])
      toast.success('Удалено успешно')
      setDeleteModal({ open: false, id: null, name: '', type: '' })
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка удаления')
    } finally {
      setDeleting(false)
    }
  }

  const handleDeleteItem = (item, type) => {
    setDeleteModal({ open: true, id: item.id, name: item.name, type })
  }

  const filterTabs = [
    { value: 'all', label: 'Все', count: stats.depts },
    { value: 'kafedra', label: 'Кафедры', count: stats.kafedras },
    { value: 'otdel', label: 'Отделы', count: stats.otdels },
  ]

  return (
    <div className="min-h-screen bg-[#F8F9FB] p-4 md:p-6 lg:p-12 text-slate-900 font-sans selection:bg-indigo-100">
      <header className="max-w-[1600px] mx-auto mb-6 flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <div className="flex items-center gap-3 mb-2 md:mb-4">
            <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-100 shrink-0">
              <ShieldCheck className="text-white w-6 h-6" />
            </div>
            <div>
              <h1 className="text-base md:text-lg font-black tracking-tight text-slate-900 leading-none">UNIVER.STRUCTURE</h1>
              <p className="text-[9px] font-black uppercase tracking-[0.3em] text-slate-400 mt-1">Управление структурой</p>
            </div>
          </div>
          <h2 className="text-2xl md:text-5xl font-black text-slate-900 tracking-tighter leading-none mb-1 md:mb-3">Кафедры и отделы.</h2>
        </div>
        <div className="flex flex-wrap items-center gap-2 md:gap-3">
          <button onClick={() => setClassModal({ open: true, data: null, directionId: null })}
            className="flex-1 md:flex-none flex items-center justify-center gap-2 px-3 md:px-5 py-2.5 md:py-3 rounded-2xl border border-slate-200 bg-white text-[10px] font-black uppercase tracking-widest text-slate-600 hover:bg-slate-50 transition-all shadow-sm">
            <GraduationCap size={16} className="text-violet-500 shrink-0" /> Группа
          </button>
          <button onClick={() => setDirectionModal({ open: true, data: null, deptId: null })}
            className="flex-1 md:flex-none flex items-center justify-center gap-2 px-3 md:px-5 py-2.5 md:py-3 rounded-2xl border border-slate-200 bg-white text-[10px] font-black uppercase tracking-widest text-slate-600 hover:bg-slate-50 transition-all shadow-sm">
            <Compass size={16} className="text-indigo-500 shrink-0" /> Направление
          </button>
          <button onClick={() => setPositionModal({ open: true, data: null, deptId: null })}
            className="flex-1 md:flex-none flex items-center justify-center gap-2 px-3 md:px-5 py-2.5 md:py-3 rounded-2xl border border-slate-200 bg-white text-[10px] font-black uppercase tracking-widest text-slate-600 hover:bg-slate-50 transition-all shadow-sm">
            <Briefcase size={16} className="text-blue-500 shrink-0" /> Должность
          </button>
          <button onClick={() => setDeptModal({ open: true, data: null })}
            className="w-full md:w-auto flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-slate-900 text-white text-[10px] font-black uppercase tracking-[0.2em] shadow-xl shadow-slate-200 hover:bg-slate-800 transition-all">
            <Plus size={18} /> Кафедра
          </button>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto space-y-6 md:space-y-8">
        <section className="flex md:grid md:grid-cols-5 gap-4 overflow-x-auto hide-scrollbar snap-x pb-2">
          <div className="snap-start shrink-0 w-40 md:w-auto"><SummaryStat label="Кафедры" value={stats.kafedras} colorClass="amber" icon={Building2} /></div>
          <div className="snap-start shrink-0 w-40 md:w-auto"><SummaryStat label="Направления" value={stats.directions} colorClass="indigo" icon={Compass} /></div>
          <div className="snap-start shrink-0 w-40 md:w-auto"><SummaryStat label="Группы" value={stats.groups} colorClass="violet" icon={GraduationCap} /></div>
          <div className="snap-start shrink-0 w-40 md:w-auto"><SummaryStat label="Студенты" value={stats.students} colorClass="emerald" icon={Users} /></div>
          <div className="snap-start shrink-0 w-40 md:w-auto"><SummaryStat label="Работники" value={employeesCount} colorClass="indigo" icon={Briefcase} /></div>
        </section>

        {/* Toolbar: Filter + Search + View Toggle */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="w-full overflow-x-auto hide-scrollbar pb-1 sm:pb-0">
            <FilterTabs value={filter} onChange={setFilter} tabs={filterTabs} />
          </div>
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <div className="relative flex-1 sm:min-w-[280px]">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" />
              <input
                className="bg-white border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm font-bold text-slate-900 outline-none focus:border-indigo-600 focus:ring-4 focus:ring-indigo-600/5 transition-all w-full shadow-sm"
                placeholder="Поиск..."
                value={search} onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <ViewToggle view={view} onChange={setView} />
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[1,2,3,4,5,6,7,8].map(i => <div key={i} className="h-36 bg-white border border-slate-200 rounded-2xl animate-pulse" />)}
          </div>
        ) : filteredHierarchy.length === 0 ? (
          <EmptyState onReset={() => { setSearch(''); setFilter('all') }} />
        ) : view === 'kanban' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredHierarchy.map(dept => (
              <DeptKanbanCard key={dept.id} dept={dept} navigate={navigate}
                onEdit={(d) => setDeptModal({ open: true, data: d })}
                onDelete={(d) => handleDeleteItem(d, 'dept')}
                onAddDirection={(deptId) => setDirectionModal({ open: true, data: null, deptId })}
                onAddPosition={(deptId) => setPositionModal({ open: true, data: null, deptId })} />
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {filteredHierarchy.map(dept => (
              <DeptListRow key={dept.id} dept={dept} navigate={navigate}
                onEdit={(d) => setDeptModal({ open: true, data: d })}
                onDelete={(d) => handleDeleteItem(d, 'dept')}
                onAddDirection={(deptId) => setDirectionModal({ open: true, data: null, deptId })}
                onAddPosition={(deptId) => setPositionModal({ open: true, data: null, deptId })} />
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      <DepartmentModal open={deptModal.open}
        onClose={() => { setDeptModal({ open: false, data: null }); qc.invalidateQueries(['departments-hierarchy']) }}
        department={deptModal.data} />
      <DirectionModal open={directionModal.open}
        onClose={() => { setDirectionModal({ open: false, data: null, deptId: null }); qc.invalidateQueries(['departments-hierarchy']) }}
        direction={directionModal.data} defaultDepartment={directionModal.deptId} />
      <ClassModal open={classModal.open}
        onClose={() => { setClassModal({ open: false, data: null, directionId: null }); qc.invalidateQueries(['departments-hierarchy']) }}
        schoolClass={classModal.data} defaultDirection={classModal.directionId} />
      <PositionModal open={positionModal.open}
        onClose={() => setPositionModal({ open: false, data: null, deptId: null })}
        position={positionModal.data} defaultDepartment={positionModal.deptId} />

      <Modal open={deleteModal.open}
        onClose={() => !deleting && setDeleteModal({ open: false, id: null, name: '', type: '' })}
        title="Подтверждение удаления" size="sm"
        footer={
          <div className="flex gap-4 w-full px-4 pb-4">
            <button className="flex-1 py-4 rounded-2xl border border-slate-200 font-black text-[11px] uppercase tracking-widest text-slate-600 hover:bg-slate-50 transition-all"
              onClick={() => setDeleteModal({ open: false, id: null, name: '', type: '' })} disabled={deleting}>Отмена</button>
            <button className="flex-1 py-4 rounded-2xl bg-rose-600 text-white font-black text-[11px] uppercase tracking-widest hover:bg-rose-700 shadow-xl shadow-rose-200 transition-all"
              onClick={handleDelete} disabled={deleting}>{deleting ? 'Удаление...' : 'Удалить'}</button>
          </div>
        }>
        <div className="text-center py-8">
          <div className="w-20 h-20 bg-rose-50 text-rose-500 rounded-3xl flex items-center justify-center mx-auto mb-6 border border-rose-100">
            <Trash2 size={36} strokeWidth={2.5} />
          </div>
          <p className="text-slate-500 font-medium px-4">
            Удалить <span className="text-slate-900 font-black">«{deleteModal.name}»</span>?
          </p>
        </div>
      </Modal>
    </div>
  )
}

function EmptyState({ onReset }) {
  return (
    <div className="bg-white border border-slate-200 rounded-[48px] p-24 text-center shadow-sm">
      <div className="w-24 h-24 bg-slate-50 rounded-3xl flex items-center justify-center mx-auto mb-8 border border-slate-100">
        <LayoutGrid size={48} className="text-slate-200" />
      </div>
      <h3 className="text-2xl font-black text-slate-900 mb-3 tracking-tighter">Данные не найдены</h3>
      <button onClick={onReset}
        className="px-8 py-3 rounded-2xl border-2 border-slate-900 font-black text-[10px] uppercase tracking-widest text-slate-900 hover:bg-slate-900 hover:text-white transition-all">
        Сбросить
      </button>
    </div>
  )
}
